/**
 * 生成任务事件桥接层（video / hunyuan3d 等 WebSocket 生成链路共用）。
 *
 * 职责：
 * - createGenerationTaskStarter：以当前用户/画板上下文创建 startTask（各域 initXxxTaskDeps 共用）
 * - bridgeTaskProgressToStore：SimulationStage → MESSAGE_STATUS，转发 progress 事件到 taskEventStore
 * - bridgeTaskErrorToStore：并发任务冲突转 cancel、其余错误解析文案后转 failed
 *
 * NOTE: 新增生成域（音频等）时应复用本模块，而不是复制各域 task.ts 中的桥接代码。
 */

import { SimulationStage } from '@xmiles/holopix-ai'
import { useUserStore } from '@/store/user'
import { useTaskEventStore } from './store/taskEvent'
import { useBoardCanvasStore } from './store/board'
import {
  useArtworkGenerationTaskManager,
  handleGenerationError,
  ERROR_STATUS,
} from '../hooks/useArtworkGenerationTaskManager'
import { MESSAGE_STATUS } from '../components/ChatPanel/types'

/**
 * 创建绑定当前用户/画板上下文的任务启动器。
 * NOTE: 内部访问 Pinia store，需在 Pinia 初始化后调用（画布初始化时机）。
 */
export const createGenerationTaskStarter = () => {
  const userStore = useUserStore()
  const boardCanvasStore = useBoardCanvasStore()
  const { startTask } = useArtworkGenerationTaskManager(() => ({
    userId: userStore.userInfo.id,
    boardId: Number(boardCanvasStore.boardId || 0),
    artworkId: Number(boardCanvasStore.artworkId || 0),
    uniqueId: userStore.userInfo.uniqueId || '',
  }))
  return startTask
}

/** SimulationStage → 聊天消息状态（初始化/排队 → 排队中，其余 → 生成中） */
export const resolveTaskProgressStatus = (stage: SimulationStage) => {
  return [SimulationStage.INITIALIZING, SimulationStage.QUEUING].includes(stage)
    ? MESSAGE_STATUS.QUEUING
    : MESSAGE_STATUS.GENERATING
}

/** 将任务进度事件转发到 taskEventStore（progress） */
export const bridgeTaskProgressToStore = (state: any, clientId: string) => {
  useTaskEventStore().emit('progress', {
    clientId,
    status: resolveTaskProgressStatus(state.stage),
  } as any)
}

/**
 * 将任务错误事件转发到 taskEventStore。
 * 业务规则：并发任务冲突（CONCURRENT_TASK）按「取消」处理而非失败，不弹错误消息。
 * @returns 是否按取消处理（供调用方决定是否继续走自身的 onError 回调）
 */
export const bridgeTaskErrorToStore = (
  errorInfo: any,
  fallbackClientId: string,
  options?: { vipChannel?: boolean },
): boolean => {
  const taskEventStore = useTaskEventStore()
  const clientId = errorInfo.clientId || fallbackClientId
  if (errorInfo.status === ERROR_STATUS.CONCURRENT_TASK) {
    taskEventStore.emit('cancel', { clientId } as any)
    return true
  }
  const resolvedMsg = handleGenerationError(errorInfo, { vipChannel: options?.vipChannel })
  taskEventStore.emit('failed', {
    clientId,
    status: errorInfo.status,
    message: resolvedMsg,
  } as any)
  return false
}
